import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, MapPin, Shield, Briefcase, Plane, DollarSign, Calendar, Globe, ArrowRight, CheckCircle2, Film, Users, Award, Download } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { SEO } from '@/components/SEO';
import { VideoMontage } from '@/components/VideoMontage';
import { LocationGallery } from '@/components/LocationGallery';
import { CaseStudies } from '@/components/CaseStudies';

// Quick Contact Modal Component
function QuickContactModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    project_type: '',
    shoot_location: '',
    shooting_date: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Please provide your name and email');
      return;
    }

    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          message: `[MEXICO PRODUCTION INQUIRY]
Project Type: ${formData.project_type || 'Not specified'}
Country of Origin: ${formData.country || 'Not specified'}
Shoot Location: ${formData.shoot_location || 'Not specified'}
Shooting Date: ${formData.shooting_date || 'Not specified'}

${formData.message || 'No additional message provided'}`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit inquiry');
      }

      setSubmitted(true);
      toast.success('Inquiry sent successfully! We\'ll be in touch within 24 hours.');
      
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          country: '',
          project_type: '',
          shoot_location: '',
          shooting_date: '',
          message: ''
        });
      }, 2000);
    } catch (err) {
      toast.error('Something went wrong. Please try again or email us directly at paco@baklight.com');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {submitted ? (
          <div className="text-center py-12">
            <CheckCircle2 size={48} className="text-[hsl(var(--bk-success))] mx-auto mb-4" />
            <h3 className="font-display text-2xl text-[hsl(var(--bk-fg))] mb-2">Thank You!</h3>
            <p className="font-body text-[hsl(var(--bk-fg-muted))]">We'll be in touch within 24 hours.</p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-display text-2xl text-[hsl(var(--bk-fg))] mb-2">Talk to a Mexico Producer</h3>
                <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">Fast response • Zero obligation • Confidential</p>
              </div>
              <button onClick={onClose} className="text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))] text-2xl leading-none">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your name"
                    className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))]"
                  />
                </div>
                <div>
                  <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">Country of Origin</Label>
                  <Select value={formData.country} onValueChange={(v) => setFormData({...formData, country: v})}>
                    <SelectTrigger className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110]">
                      <SelectItem value="usa">United States</SelectItem>
                      <SelectItem value="canada">Canada</SelectItem>
                      <SelectItem value="uk">United Kingdom</SelectItem>
                      <SelectItem value="germany">Germany</SelectItem>
                      <SelectItem value="france">France</SelectItem>
                      <SelectItem value="spain">Spain</SelectItem>
                      <SelectItem value="italy">Italy</SelectItem>
                      <SelectItem value="japan">Japan</SelectItem>
                      <SelectItem value="australia">Australia</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">Project Type</Label>
                  <Select value={formData.project_type} onValueChange={(v) => setFormData({...formData, project_type: v})}>
                    <SelectTrigger className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110]">
                      <SelectItem value="commercial">Commercial / Advertising</SelectItem>
                      <SelectItem value="documentary">Documentary</SelectItem>
                      <SelectItem value="fiction">Fiction / Feature</SelectItem>
                      <SelectItem value="series">Series / Episodic</SelectItem>
                      <SelectItem value="animation">Animation / VFX</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">Shoot Location (City/State)</Label>
                <Input
                  value={formData.shoot_location}
                  onChange={(e) => setFormData({...formData, shoot_location: e.target.value})}
                  placeholder="e.g., Guadalajara, Jalisco"
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))]"
                />
              </div>

              <div>
                <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">Shooting Date (if known)</Label>
                <div className="relative">
                  <Input
                    type="date"
                    value={formData.shooting_date}
                    onChange={(e) => setFormData({...formData, shooting_date: e.target.value})}
                    className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] [color-scheme:dark]"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>
              </div>

              <div>
                <Label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">Tell us about your project</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Brief overview of your production needs..."
                  rows={3}
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full font-body text-sm px-6 py-3 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? 'Sending...' : 'Send Inquiry'} <ArrowRight size={16} />
              </button>

              <p className="text-xs text-center text-[hsl(var(--bk-fg-subtle))] mt-4">
                Or email directly: <a href="mailto:paco@baklight.com" className="text-[hsl(var(--bk-accent))] hover:opacity-80">paco@baklight.com</a>
              </p>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}

// Expandable Section Component
function ExpandableSection({ icon: Icon, title, preview, children, isOpen, onToggle }) {
  return (
    <div className="border border-[hsl(var(--bk-line))] rounded-lg overflow-hidden bg-[hsl(var(--bk-bg-elev))]">
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 flex items-start gap-4 text-left hover:bg-[hsl(var(--bk-bg))]/50 transition-colors"
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[hsl(var(--bk-accent))]/10 flex items-center justify-center">
          <Icon size={20} className="text-[hsl(var(--bk-accent))]" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-1">{title}</h3>
          <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{preview}</p>
        </div>
        <ChevronDown
          size={20}
          className={`text-[hsl(var(--bk-fg-muted))] transition-transform flex-shrink-0 mt-1 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 pt-2 border-t border-[hsl(var(--bk-line))]">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function MexicoPage() {
  const [openSections, setOpenSections] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const toggleSection = (id) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openGallery = (location) => {
    setSelectedLocation(location);
    setGalleryOpen(true);
  };

  return (
    <div>
      <SEO
        title="Film Production in Mexico — Production Services & Production Incentives | Baklight"
        description="Complete Mexico film production services with EFICINE federal rebate and Jalisco state incentive programs. Location scouting, permits, crew, equipment rental, and production support across Mexico's most cinematic regions."
        path="/mexico"
        keywords="film production mexico, mexico production services, film incentives mexico, jalisco film rebate, eficine, mexico location scouting, guadalajara film production, mexico crew rental, international production mexico, shoot in mexico, mexico film permits, film incentives mexico, puerto vallarta filming, mexico cinematography, production company mexico"
        image="/assets/og-mexico-production.jpg"
      />

      {/* Structured Data for Mexico Production Services */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Film Production Services",
          "provider": {
            "@type": "Organization",
            "name": "Baklight",
            "url": "https://baklight.com",
            "logo": "https://baklight.com/assets/logo.png",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Guadalajara",
              "addressRegion": "Jalisco",
              "addressCountry": "MX"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+52-33-3808-3824",
              "contactType": "customer service",
              "email": "paco@baklight.com",
              "areaServed": ["MX", "US", "CA", "GB"],
              "availableLanguage": ["en", "es"]
            }
          },
          "areaServed": {
            "@type": "Country",
            "name": "Mexico"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Production Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Location Scouting & Management",
                  "description": "Professional location scouting across Mexico's diverse regions including UNESCO heritage sites, coastal resorts, and urban centers"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Film Permits & Government Liaisons",
                  "description": "Comprehensive permit acquisition and coordination with local authorities, EFICINE tax credit processing"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Local Crew & Equipment Rental",
                  "description": "Full cinematography crews, RED/ARRI camera packages, lighting, grip, and sound equipment rental in Mexico"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Production Coordination",
                  "description": "Bilingual production coordinators, transportation, accommodation, and logistical support for international productions"
                }
              }
            ]
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5.0",
            "reviewCount": "47"
          }
        })}
      </script>

      {/* Structured Data for Mexico Production Guide (PDF Download) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DigitalDocument",
          "name": "Mexico Production Guide 2026 — Complete Resource for Filming in Mexico",
          "description": "17-page comprehensive guide covering film incentives, location overviews, permit processes, crew rates, equipment costs, and production best practices for shooting in Mexico.",
          "author": {
            "@type": "Organization",
            "name": "Baklight"
          },
          "datePublished": "2026-01-01",
          "inLanguage": "en",
          "fileFormat": "application/pdf",
          "keywords": ["mexico film production", "film incentives mexico", "eficine", "jalisco film rebate", "mexico location guide", "production guide mexico"],
          "about": [
            {
              "@type": "Thing",
              "name": "Film Incentives",
              "description": "30% federal EFICINE tax credit and 40% Jalisco state film rebate"
            },
            {
              "@type": "Thing",
              "name": "Production Locations",
              "description": "Comprehensive overview of filming locations across Mexico including Jalisco, Oaxaca, Mexico City, and coastal regions"
            },
            {
              "@type": "Thing",
              "name": "Production Resources",
              "description": "Local crew rates, equipment rental costs, permit requirements, and logistical guidelines"
            }
          ],
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          }
        })}
      </script>

      {/* Hero with Video Montage */}
      <section className="relative min-h-[75vh] flex items-center overflow-hidden">
        {/* Video Background */}
        <VideoMontage />
        
        <div className="relative z-10 max-w-[1200px] mx-auto px-5 sm:px-8 py-32">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-4">Production Services</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-[-0.03em] leading-[0.9] text-[hsl(var(--bk-fg))] mb-6 max-w-[900px]">
              Shoot in Mexico.<br/>Keep more of your budget.
            </h1>
            <p className="font-body text-lg sm:text-xl text-[hsl(var(--bk-fg-muted))] max-w-[600px] mb-8 leading-relaxed">
              Up to 30% federal tax credit + 40% state rebate. World-class locations. Experienced crews. One production partner.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setModalOpen(true)}
                className="font-body text-sm px-8 py-3 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity inline-flex items-center gap-2"
              >
                Talk to a Mexico Producer <ArrowRight size={16} />
              </button>
              <a
                href="#how-it-works"
                className="font-body text-sm px-8 py-3 border border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] rounded-[var(--radius)] hover:bg-[hsl(var(--bk-fg))]/10 transition-colors"
              >
                How It Works
              </a>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Quick Value Props */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <SectionReveal delay={0}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--bk-accent))]/10 flex items-center justify-center mx-auto mb-4">
                  <DollarSign size={28} className="text-[hsl(var(--bk-accent))]" />
                </div>
                <h3 className="font-display text-4xl text-[hsl(var(--bk-fg))] mb-2">Up to 70%</h3>
                <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">Combined incentives<br/>(Federal + State)</p>
              </div>
            </SectionReveal>
            <SectionReveal delay={0.1}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--bk-accent))]/10 flex items-center justify-center mx-auto mb-4">
                  <Film size={28} className="text-[hsl(var(--bk-accent))]" />
                </div>
                <h3 className="font-display text-4xl text-[hsl(var(--bk-fg))] mb-2">25+ Years</h3>
                <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">Producing in Jalisco<br/>& across Mexico</p>
              </div>
            </SectionReveal>
            <SectionReveal delay={0.2}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--bk-accent))]/10 flex items-center justify-center mx-auto mb-4">
                  <Users size={28} className="text-[hsl(var(--bk-accent))]" />
                </div>
                <h3 className="font-display text-4xl text-[hsl(var(--bk-fg))] mb-2">50+ Crew</h3>
                <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">Bilingual, experienced<br/>production team</p>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* Quick Concerns - First Layer */}
      <section id="how-it-works" className="py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-3">Your Questions, Answered</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--bk-fg))] mb-4 leading-tight">
              Everything you need to know about<br/>filming in Mexico
            </h2>
            <p className="font-body text-lg text-[hsl(var(--bk-fg-muted))] mb-12 max-w-[700px]">
              We've worked with producers from 20+ countries. Here are the answers to your most common questions.
            </p>
          </SectionReveal>

          <div className="space-y-4">
            <SectionReveal delay={0.1}>
              <ExpandableSection
                icon={DollarSign}
                title="Who can give me money?"
                preview="Up to 30% federal tax credit (EFICA) + 40% state cash rebate (Filma Jalisco). Stackable incentives that can cover up to 70% of qualifying spend."
                isOpen={openSections.incentives}
                onToggle={() => toggleSection('incentives')}
              >
                <div className="space-y-6">
                  <div>
                    <h4 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-3">EFICA — Federal Tax Credit (2026)</h4>
                    <div className="bg-[hsl(var(--bk-bg))] p-5 rounded-lg border border-[hsl(var(--bk-line))] mb-4">
                      <ul className="space-y-2 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                        <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> <span><strong className="text-[hsl(var(--bk-fg))]">30% transferable tax credit</strong> on eligible costs incurred in Mexico</span></li>
                        <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> <span><strong className="text-[hsl(var(--bk-fg))]">Cap:</strong> 40 million pesos per project (~$2M USD)</span></li>
                        <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> <span><strong className="text-[hsl(var(--bk-fg))]">Minimum spend:</strong> 40M MXN (features), 20M MXN (docs), 5M MXN (VFX/post)</span></li>
                        <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> <span><strong className="text-[hsl(var(--bk-fg))]">Requirement:</strong> 70% of supplies must be sourced from Mexican providers</span></li>
                        <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> <span>Active until September 2030</span></li>
                      </ul>
                    </div>
                    <p className="font-body text-sm text-[hsl(var(--bk-fg-subtle))] italic">Baklight handles the entire EFICA application process, compliance tracking, and cost verification.</p>
                  </div>

                  <Separator className="bg-[hsl(var(--bk-line))]" />

                  <div>
                    <h4 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-3">Filma Jalisco — State Cash Rebate (2026)</h4>
                    <div className="bg-[hsl(var(--bk-bg))] p-5 rounded-lg border border-[hsl(var(--bk-line))] mb-4">
                      <ul className="space-y-2 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                        <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> <span><strong className="text-[hsl(var(--bk-fg))]">40% rebate</strong> on audiovisual services + <strong className="text-[hsl(var(--bk-fg))]">20% on logistics</strong></span></li>
                        <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> <span><strong className="text-[hsl(var(--bk-fg))]">Cap:</strong> 20 million pesos per project (~$1M USD)</span></li>
                        <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> <span><strong className="text-[hsl(var(--bk-fg))]">Requirement:</strong> Must work with a Jalisco-based production company (Production Services Agreement)</span></li>
                        <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> <span>70M MXN allocated for 2026 cycle</span></li>
                      </ul>
                    </div>
                    <p className="font-body text-sm text-[hsl(var(--bk-fg-subtle))] italic">Baklight is a formally registered Jalisco production company and handles your PSA and rebate application.</p>
                  </div>

                  <div className="p-5 rounded-lg bg-[hsl(var(--bk-accent))]/5 border border-[hsl(var(--bk-accent))]/20">
                    <p className="font-body text-sm text-[hsl(var(--bk-fg))]">
                      <strong>Bottom line:</strong> International productions can stack both incentives to recover up to 70% of eligible spend. Your country of origin affects eligibility — <button onClick={() => setModalOpen(true)} className="text-[hsl(var(--bk-accent))] hover:opacity-80 underline">contact us</button> for a specific breakdown.
                    </p>
                  </div>
                </div>
              </ExpandableSection>
            </SectionReveal>

            <SectionReveal delay={0.15}>
              <ExpandableSection
                icon={Shield}
                title="How safe is it?"
                preview="Jalisco is one of Mexico's most production-friendly states. We maintain relationships with local authorities and provide full on-set safety protocols."
                isOpen={openSections.safety}
                onToggle={() => toggleSection('safety')}
              >
                <div className="space-y-4">
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">
                    Guadalajara and the broader Jalisco region are among Mexico's most developed and production-friendly areas. We've successfully produced for HBO, National Geographic, BBC, Amazon, and major international brands with zero security incidents.
                  </p>
                  <div className="bg-[hsl(var(--bk-bg))] p-5 rounded-lg border border-[hsl(var(--bk-line))]">
                    <h5 className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-3">Our Safety Protocols:</h5>
                    <ul className="space-y-2 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                      <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> Pre-production location scouting and risk assessment</li>
                      <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> Local fixers and community liaisons</li>
                      <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> Dedicated production security when needed</li>
                      <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> 24/7 production support and emergency protocols</li>
                      <li className="flex items-start gap-2"><ChevronRight size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" /> Insurance and liability coverage coordination</li>
                    </ul>
                  </div>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-subtle))] italic">
                    We work closely with the Jalisco Film Commission and local authorities to ensure smooth, safe productions. Our 25+ years of experience means we know which locations work best and when.
                  </p>
                </div>
              </ExpandableSection>
            </SectionReveal>

            <SectionReveal delay={0.2}>
              <ExpandableSection
                icon={Briefcase}
                title="Can I travel with my gear?"
                preview="Yes. Use an ATA Carnet for temporary duty-free import of professional equipment for up to 6 months. We handle the registration and customs process."
                isOpen={openSections.gear}
                onToggle={() => toggleSection('gear')}
              >
                <div className="space-y-4">
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">
                    You can bring your own camera package, lighting, and professional gear into Mexico duty-free using an <strong className="text-[hsl(var(--bk-fg))]">ATA Carnet</strong> — a temporary import document that works like a passport for equipment.
                  </p>
                  <div className="bg-[hsl(var(--bk-bg))] p-5 rounded-lg border border-[hsl(var(--bk-line))]">
                    <h5 className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-3">ATA Carnet Process:</h5>
                    <ol className="space-y-3 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">1</span>
                        <span>Obtain ATA Carnet from your local chamber of commerce (US, Canada, EU, etc.)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">2</span>
                        <span>Provide detailed equipment list with serial numbers and values</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">3</span>
                        <span><strong className="text-[hsl(var(--bk-fg))]">Baklight registers your Carnet</strong> with CANACO (Mexico City Chamber) — <em>mandatory pre-arrival step</em></span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">4</span>
                        <span>Get entry/exit stamps at Mexican customs (we coordinate this)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">5</span>
                        <span>Valid for up to 6 months</span>
                      </li>
                    </ol>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="font-body text-sm text-amber-200">
                      <strong>Important:</strong> Drones, weaponry, and certain regulated items require separate permits and cannot be imported via ATA Carnet. We handle these specialized permits separately.
                    </p>
                  </div>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-subtle))] italic">
                    Alternative: Rent from Baklight's equipment inventory (RED, ARRI, Sony cinema cameras + full lighting/grip packages) to avoid customs entirely.
                  </p>
                </div>
              </ExpandableSection>
            </SectionReveal>

            <SectionReveal delay={0.25}>
              <ExpandableSection
                icon={Users}
                title="Bring my crew or hire local?"
                preview="Hybrid approach works best. Bring key creatives, hire experienced local crew for G&E, production, and support. Bilingual, agency-trained teams."
                isOpen={openSections.crew}
                onToggle={() => toggleSection('crew')}
              >
                <div className="space-y-4">
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">
                    Most international productions bring 2–5 key creatives (director, DP, producer, agency team) and hire local crew for everything else. This balances creative control with cost efficiency and incentive eligibility.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[hsl(var(--bk-bg))] p-5 rounded-lg border border-[hsl(var(--bk-line))]">
                      <h5 className="font-mono-meta text-xs text-[hsl(var(--bk-accent))] mb-3">Typically Bring:</h5>
                      <ul className="space-y-2 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                        <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5" /> Director</li>
                        <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5" /> DP (if specific vision required)</li>
                        <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5" /> Agency creative team</li>
                        <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5" /> Specific talent (if needed)</li>
                      </ul>
                    </div>
                    <div className="bg-[hsl(var(--bk-bg))] p-5 rounded-lg border border-[hsl(var(--bk-line))]">
                      <h5 className="font-mono-meta text-xs text-[hsl(var(--bk-accent))] mb-3">Hire Locally:</h5>
                      <ul className="space-y-2 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                        <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5" /> Line producer & production manager</li>
                        <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5" /> Camera operators & AC team</li>
                        <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5" /> Grip & electric crew</li>
                        <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5" /> Art dept, wardrobe, HMU</li>
                        <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5" /> Production assistants & coordinators</li>
                      </ul>
                    </div>
                  </div>
                  <div className="p-5 rounded-lg bg-[hsl(var(--bk-accent))]/5 border border-[hsl(var(--bk-accent))]/20">
                    <p className="font-body text-sm text-[hsl(var(--bk-fg))]">
                      <strong>Baklight's crew network includes:</strong> 50+ experienced bilingual professionals who've worked with major international brands, streamers, and agencies. Agency-ready workflows, fast turnarounds, and remote collaboration expertise.
                    </p>
                  </div>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-subtle))] italic">
                    Hiring local crew also maximizes your incentive eligibility (both EFICA and Filma Jalisco prioritize local spend).
                  </p>
                </div>
              </ExpandableSection>
            </SectionReveal>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => setModalOpen(true)}
              className="font-body text-sm px-8 py-3 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity inline-flex items-center gap-2"
            >
              Get Your Custom Production Plan <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--bk-accent))]">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8 text-center">
          <SectionReveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--bk-bg))] mb-4 leading-tight">
              Ready to explore Mexico for your next production?
            </h2>
            <p className="font-body text-lg text-[hsl(var(--bk-bg))]/80 mb-8 max-w-[600px] mx-auto">
              Let's discuss your project, timeline, and how we can maximize your incentives.
            </p>
            <button
              onClick={() => setModalOpen(true)}
              className="font-body text-sm px-8 py-3 bg-[hsl(var(--bk-bg))] text-[hsl(var(--bk-accent))] rounded-[var(--radius)] hover:opacity-90 transition-opacity inline-flex items-center gap-2"
            >
              Talk to a Mexico Producer <ArrowRight size={16} />
            </button>
          </SectionReveal>
        </div>
      </section>

      {/* Locations Showcase */}
      <section id="locations" className="py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-3">Cinematic Diversity</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--bk-fg))] mb-4 leading-tight">
              Jalisco: Multiple countries<br/>in one shooting schedule
            </h2>
            <p className="font-body text-lg text-[hsl(var(--bk-fg-muted))] mb-12 max-w-[700px]">
              From UNESCO agave landscapes to Pacific coastline, colonial architecture to modern cityscapes — all within a few hours of Guadalajara.
            </p>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <SectionReveal delay={0.1}>
              <div className="relative aspect-[4/5] rounded-lg overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1633209174553-870cdca1cfd0?w=800&q=80"
                  alt="Agave fields in Jalisco"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-2xl text-white mb-2">UNESCO Agave Landscape</h3>
                  <p className="font-body text-sm text-white/80">Iconic blue agave fields, volcanic backdrop, distilleries</p>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal delay={0.15}>
              <div className="relative aspect-[4/5] rounded-lg overflow-hidden">
                <img
                  src="/assets/mexico/crew-02.jpg"
                  alt="Film production in Guadalajara"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-2xl text-white mb-2">Modern Guadalajara</h3>
                  <p className="font-body text-sm text-white/80">Contemporary architecture, business districts, urban lifestyle</p>
                </div>
              </div>
            </SectionReveal>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Pacific Coast', image: 'https://images.unsplash.com/photo-1662044069225-ab6383ba03bd?w=600&q=80' },
              { name: 'Colonial Towns', image: 'https://images.unsplash.com/photo-1709065130843-7d68eff4c0b8?w=600&q=80' },
              { name: 'Mountain Forests', image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&q=80' },
              { name: 'Canyons & Desert', image: 'https://images.unsplash.com/photo-1666198045839-e484533e97d1?w=600&q=80' },
              { name: 'Lake Districts', image: 'https://images.unsplash.com/photo-1693277675926-de9cdcce5316?w=600&q=80' },
              { name: 'Industrial Zones', image: 'https://images.unsplash.com/photo-1648094323904-d6ae0a74838e?w=600&q=80' },
              { name: 'Historic Centers', image: 'https://images.unsplash.com/photo-1588191969960-ff8be4d184c6?w=600&q=80' },
              { name: 'Rural Villages', image: 'https://images.unsplash.com/photo-1658778177301-0f71c17624ed?w=600&q=80' }
            ].map((location, i) => (
              <SectionReveal key={i} delay={0.05 * i}>
                <button
                  onClick={() => openGallery(location.name)}
                  className="group relative overflow-hidden rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg-elev))] hover:border-[hsl(var(--bk-accent))]/50 transition-all aspect-[4/3] w-full"
                >
                  <img 
                    src={location.image} 
                    alt={location.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="font-body text-sm text-white mb-1">{location.name}</p>
                    <p className="font-body text-xs text-white/60 group-hover:text-[hsl(var(--bk-accent))] transition-colors">View gallery →</p>
                  </div>
                </button>
              </SectionReveal>
            ))}
          </div>

          <div className="mt-12 p-6 rounded-lg bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))]">
            <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">
              <strong className="text-[hsl(var(--bk-fg))]">All locations within 2-3 hours of Guadalajara International Airport.</strong> We handle location scouting, permits, and logistics for every terrain type — from UNESCO heritage sites to private haciendas and coastal resorts.
            </p>
          </div>
        </div>
      </section>

      {/* Production Infrastructure */}
      <section className="py-20 sm:py-28 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-3">Full-Service Production</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--bk-fg))] mb-12 leading-tight">
              Everything you need,<br/>one production partner
            </h2>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <SectionReveal delay={0.1}>
              <div className="p-6 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded-lg">
                <Film size={28} className="text-[hsl(var(--bk-accent))] mb-4" />
                <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-3">Production Services</h3>
                <ul className="space-y-2 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Line producing & budgeting</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Location scouting & management</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Permits & government liaisons</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Casting & talent management</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Transportation & logistics</li>
                </ul>
              </div>
            </SectionReveal>

            <SectionReveal delay={0.15}>
              <div className="p-6 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded-lg">
                <Users size={28} className="text-[hsl(var(--bk-accent))] mb-4" />
                <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-3">Local Crew</h3>
                <ul className="space-y-2 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Directors of Photography & cinematographers</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Production coordinators & fixers</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Gaffers, grips & electric teams</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Production designers & set builders</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Hair, makeup & wardrobe stylists</li>
                </ul>
              </div>
            </SectionReveal>

            <SectionReveal delay={0.2}>
              <div className="p-6 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded-lg">
                <Briefcase size={28} className="text-[hsl(var(--bk-accent))] mb-4" />
                <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-3">Equipment</h3>
                <ul className="space-y-2 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> RED, ARRI, Sony cinema cameras</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Cinema lenses (SIGMA, Tokina, Canon)</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Full lighting & grip packages</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Certified drone operators</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Sound & monitoring equipment</li>
                </ul>
              </div>
            </SectionReveal>

            <SectionReveal delay={0.25}>
              <div className="p-6 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded-lg">
                <Film size={28} className="text-[hsl(var(--bk-accent))] mb-4" />
                <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-3">Post-Production</h3>
                <ul className="space-y-2 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> DaVinci Resolve 4K color suite</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Offline & online editing</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> VFX & motion graphics</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Sound design & mix</li>
                  <li className="flex items-start gap-2"><ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[hsl(var(--bk-accent))]" /> Camera-to-cloud workflows</li>
                </ul>
              </div>
            </SectionReveal>
          </div>

          <div className="mt-12 text-center">
            <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] mb-6">
              View our complete capabilities
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/services" className="font-body text-sm px-6 py-2.5 border border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] rounded-[var(--radius)] hover:bg-[hsl(var(--bk-fg))]/10 transition-colors">
                Production Services
              </a>
              <a href="/rentals" className="font-body text-sm px-6 py-2.5 border border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] rounded-[var(--radius)] hover:bg-[hsl(var(--bk-fg))]/10 transition-colors">
                Equipment Rentals
              </a>
              <a href="/post" className="font-body text-sm px-6 py-2.5 border border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] rounded-[var(--radius)] hover:bg-[hsl(var(--bk-fg))]/10 transition-colors">
                Post & Finishing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-8 text-center">Trusted by international brands & producers</p>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 opacity-70 hover:opacity-100 transition-all duration-500">
              <img src="/assets/clients-final/patron.png" alt="Patrón" className="h-10 object-contain brightness-0 invert" data-testid="client-logo-patron" />
              <img src="/assets/clients-final/hbo-white.svg" alt="HBO" className="h-10 object-contain" data-testid="client-logo-hbo" />
              <img src="/assets/clients-final/natgeo.png" alt="National Geographic" className="h-10 object-contain brightness-0 invert" data-testid="client-logo-natgeo" />
              <img src="/assets/clients-final/discovery.png" alt="Discovery" className="h-10 object-contain brightness-0 invert" data-testid="client-logo-discovery" />
              <img src="/assets/clients-final/nestle-wiki.svg" alt="Nestlé" className="h-10 object-contain brightness-0 invert" data-testid="client-logo-nestle" />
              <img src="/assets/clients-final/gatorade.png" alt="Gatorade" className="h-10 object-contain brightness-0 invert" data-testid="client-logo-gatorade" />
            </div>
            <div className="text-center mt-6">
              <a href="/clients" className="font-body text-sm text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity">
                View all clients →
              </a>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Case Studies */}
      <CaseStudies />

      {/* Download PDF Guide */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[hsl(var(--bk-accent))]/10 via-[hsl(var(--bk-bg-elev))] to-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-accent))]/20 p-8 sm:p-12">
            <SectionReveal>
              <div className="relative z-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-[hsl(var(--bk-accent))]/10 flex items-center justify-center flex-shrink-0">
                    <Download size={28} className="text-[hsl(var(--bk-accent))]" />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl sm:text-3xl text-[hsl(var(--bk-fg))] mb-2">
                      Mexico Production Guide 2026
                    </h3>
                    <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                      Everything you need: incentives breakdown, location maps, crew rates, permit checklists, and budget templates.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="p-4 rounded-lg bg-[hsl(var(--bk-bg))]/50 border border-[hsl(var(--bk-line))]">
                    <p className="font-body text-2xl font-bold text-[hsl(var(--bk-fg))] mb-1">17 Pages</p>
                    <p className="font-body text-xs text-[hsl(var(--bk-fg-subtle))]">Comprehensive guide</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[hsl(var(--bk-bg))]/50 border border-[hsl(var(--bk-line))]">
                    <p className="font-body text-2xl font-bold text-[hsl(var(--bk-fg))] mb-1">Updated</p>
                    <p className="font-body text-xs text-[hsl(var(--bk-fg-subtle))]">2026 incentives</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[hsl(var(--bk-bg))]/50 border border-[hsl(var(--bk-line))]">
                    <p className="font-body text-2xl font-bold text-[hsl(var(--bk-fg))] mb-1">50+ Tips</p>
                    <p className="font-body text-xs text-[hsl(var(--bk-fg-subtle))]">Insider knowledge</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[hsl(var(--bk-bg))]/50 border border-[hsl(var(--bk-line))]">
                    <p className="font-body text-2xl font-bold text-[hsl(var(--bk-fg))] mb-1">Free</p>
                    <p className="font-body text-xs text-[hsl(var(--bk-fg-subtle))]">No strings attached</p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    // Direct download link
                    window.open('/assets/BAKLIGHT_Mexico_Production_Guide_2026.pdf', '_blank');
                    toast.success('Opening guide! Save the file and convert to PDF if needed.');
                  }}
                  className="font-body text-sm px-8 py-3 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity inline-flex items-center gap-2"
                >
                  Download Free Guide <Download size={16} />
                </button>
                <p className="font-body text-xs text-[hsl(var(--bk-fg-subtle))] mt-4">
                  17-page PDF guide • Locations, incentives & production resources
                </p>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[800px] mx-auto px-5 sm:px-8 text-center">
          <SectionReveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--bk-fg))] mb-6 leading-tight">
              Let's make Mexico work for your production
            </h2>
            <p className="font-body text-lg text-[hsl(var(--bk-fg-muted))] mb-8">
              Whether you're scouting locations, exploring incentives, or ready to shoot — we're here to help.
            </p>
            <button
              onClick={() => setModalOpen(true)}
              className="font-body text-sm px-10 py-4 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity inline-flex items-center gap-2 text-base"
            >
              Start the Conversation <ArrowRight size={18} />
            </button>
            <p className="font-body text-sm text-[hsl(var(--bk-fg-subtle))] mt-6">
              Or email directly: <a href="mailto:paco@baklight.com" className="text-[hsl(var(--bk-accent))] hover:opacity-80">paco@baklight.com</a>
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* Quick Contact Modal */}
      <AnimatePresence>
        {modalOpen && <QuickContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />}
      </AnimatePresence>

      {/* Location Gallery Modal */}
      <LocationGallery 
        isOpen={galleryOpen} 
        onClose={() => setGalleryOpen(false)} 
        location={selectedLocation}
      />
    </div>
  );
}
